import { types } from '../../actions/backendAuth';
import { types as commonTypes } from '../../actions/backendCommon';
import { ERR_INVALID_AUTH_SCHEME, ROLE_CORE_AGENT } from '../../../constants/backend';
import { clearLoginInfo } from '../../../utils/login';
import { selectUserFromToken } from '../../../utils/jwt';
import { IS_LEGACY_LISTENER_MODE } from '../../../config/electron';
import { commonLogger } from '../../../utils/logger/loggerFactory';
import { reloadPage } from '../../../utils/browser';
import type { AuthSystemInfo, AuthUserData } from '../../types';

const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';
const IS_AUTH_WITH_PASSWORD_KEY = 'isAuthWithPassword';

const loadRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

const saveRefreshToken = (refreshToken: string | null, isAuthWithPass: boolean): void => {
    if (!refreshToken) {
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    } else {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
    }

    if (!isAuthWithPass) {
        localStorage.removeItem(IS_AUTH_WITH_PASSWORD_KEY);
    } else {
        localStorage.setItem(IS_AUTH_WITH_PASSWORD_KEY, '1');
    }
};

const isAuthWithPassword = (): boolean => !IS_LEGACY_LISTENER_MODE || !!localStorage.getItem(IS_AUTH_WITH_PASSWORD_KEY);

interface AuthState {
    token: string | null;
    userData: AuthUserData;
    refreshToken: string | null;
    systemInfo: AuthSystemInfo | null;
    authWithPassword: boolean;
    secret: string | null;
}

export const initialState: AuthState = {
    token: null,
    userData: {},
    refreshToken: loadRefreshToken(),
    systemInfo: null,
    authWithPassword: isAuthWithPassword(),
    secret: null
};

interface ReducerAction {
    type?: string;
    payload?: Record<string, unknown>;
}

interface AuthSuccessPayload {
    token?: string;
    refreshToken?: string;
    secret?: string;
    isPasswordAuth?: boolean;
}

interface AuthFailPayload {
    message?: string;
}

interface TokenRefreshPayload {
    token?: string;
}

interface ReloadPayload {
    logout?: boolean;
}

interface SystemInfoPayload {
    systemInfo?: AuthSystemInfo;
}

function toAuthSuccessPayload(payload: Record<string, unknown>): AuthSuccessPayload {
    return {
        token: typeof payload.token === 'string' ? payload.token : undefined,
        refreshToken: typeof payload.refreshToken === 'string' ? payload.refreshToken : undefined,
        secret: typeof payload.secret === 'string' ? payload.secret : undefined,
        isPasswordAuth: typeof payload.isPasswordAuth === 'boolean' ? payload.isPasswordAuth : undefined
    };
}

function toAuthFailPayload(payload: Record<string, unknown>): AuthFailPayload {
    return { message: typeof payload.message === 'string' ? payload.message : undefined };
}

function toTokenRefreshPayload(payload: Record<string, unknown>): TokenRefreshPayload {
    return { token: typeof payload.token === 'string' ? payload.token : undefined };
}

function toReloadPayload(payload: Record<string, unknown>): ReloadPayload {
    return { logout: typeof payload.logout === 'boolean' ? payload.logout : undefined };
}

function toSystemInfoPayload(payload: Record<string, unknown>): SystemInfoPayload {
    if (typeof payload.systemInfo === 'object' && payload.systemInfo !== null) {
        const systemInfo = payload.systemInfo as {
            companyName?: unknown;
            username?: unknown;
            companySecretToken?: unknown;
            uniqueId?: unknown;
            toJSON?: unknown;
        };

        return {
            systemInfo: {
                companyName: typeof systemInfo.companyName === 'string' ? systemInfo.companyName : undefined,
                username: typeof systemInfo.username === 'string' ? systemInfo.username : undefined,
                companySecretToken: typeof systemInfo.companySecretToken === 'string' ? systemInfo.companySecretToken : undefined,
                uniqueId: typeof systemInfo.uniqueId === 'string' ? systemInfo.uniqueId : undefined,
                toJSON: systemInfo.toJSON
            }
        };
    }
    return {};
}

export default function authReducer(state = initialState, { type, payload = {} }: ReducerAction = {}) {
    switch (type) {
        case types.SYSTEM_INFO_UPDATED:
        case types.SYSTEM_INFO_RECEIVED:
            return {
                ...state,
                systemInfo: toSystemInfoPayload(payload).systemInfo || null
            };
        case types.AUTH_WITH_PASSWORD_REQUIRED:
            return {
                ...state,
                authWithPassword: true
            };
        case types.AUTH_SUCCESS: {
            const { token, refreshToken, secret, isPasswordAuth } = toAuthSuccessPayload(payload);

            try {
                saveRefreshToken(refreshToken || null, !!isPasswordAuth);
            } catch (e: unknown) {
                const message = e instanceof Error ? e.message : String(e);
                if (message.indexOf('exceeded the quota') > -1) {
                    commonLogger.error(message, e);
                    localStorage.clear();
                    saveRefreshToken(refreshToken || null, !!isPasswordAuth);
                    reloadPage();
                } else {
                    throw e;
                }
            }

            const userData = selectUserFromToken(token || '');

            if (userData.role !== ROLE_CORE_AGENT) {
                localStorage.clear();
                reloadPage();
                return state;
            }

            return {
                ...state,
                userData,
                token,
                refreshToken,
                secret
            };
        }
        case types.AUTH_FAIL: {
            const authWithPassword = state.authWithPassword || toAuthFailPayload(payload).message === ERR_INVALID_AUTH_SCHEME;
            saveRefreshToken(null, authWithPassword);

            return {
                ...state,
                token: null,
                refreshToken: null,
                authWithPassword
            };
        }
        case types.TOKEN_REFRESH_SUCCESS:
            return {
                ...state,
                token: toTokenRefreshPayload(payload).token || null
            };
        case commonTypes.RELOAD:
            if (toReloadPayload(payload).logout) {
                clearLoginInfo();
                saveRefreshToken(null, false);
            }
            return state;
        default:
            return state;
    }
}
